import { useEffect, useMemo, useRef, useState } from "react";
import { useShell } from "../core/shell";
import type { Attachment, EmailMessage } from "../core/shell-types";
import { isUnsafeAttribute } from "./sanitize";

// HTML mail rendering, the browser counterpart of MailMessageBodyView on
// macOS (which loads the message into a WKWebView behind the same wrapper
// document). What WKWebView's isolation gives the native app for free, a
// browser needs spelled out:
//
// - The frame is sandboxed with no scripts, no forms, no top navigation.
//   `allow-same-origin` is deliberate and safe WITHOUT `allow-scripts`: it
//   lets the parent measure the document height and inject styles, while
//   the message itself can run nothing.
// - A CSP inside the document keeps every remote resource off (tracking
//   pixels, remote fonts, background images) until the reader asks; only
//   inline styles, data: and blob: images are allowed by default.
// - `cid:` images are inline attachments; they resolve through the shell's
//   downloadAttachment op (the same MailShellController path the native apps
//   use) into blob: URLs.
// - Links open in a new tab with no referrer.
//
// Parsing happens in an inert DOMParser document, so nothing in the message
// executes or loads while it is being prepared.

const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const FRAME_STYLE = `
:root { color-scheme: light; }
html, body { margin: 0; padding: 0; }
body {
  padding: 4px 2px 8px;
  font: 15px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #111318;
  background: #ffffff;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
img, video, iframe, table { max-width: 100%; }
img { height: auto; }
pre { white-space: pre-wrap; }
a { color: #2457d6; }
blockquote { margin: 0 0 0 0.6em; padding-left: 0.8em; border-left: 3px solid #d0d4dc; color: #444a57; }
`;

const REMOVED_ELEMENTS = [
  "script",
  "iframe",
  "frame",
  "object",
  "embed",
  "applet",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "meta",
  "link",
  "base",
  "template",
  "noscript",
];

type Prepared = {
  srcDoc: string;
  remoteImages: number;
  cids: string[];
};

function normalizeCid(raw: string): string {
  return raw.replace(/^cid:/i, "").replace(/^<|>$/g, "").trim();
}

function isRemote(url: string): boolean {
  return /^(https?:)?\/\//i.test(url.trim());
}

// Turns raw message HTML into a self-contained srcdoc. `cidUrls` maps inline
// image cids to blob: URLs already fetched; `allowRemote` opens the CSP to
// remote images and swaps their placeholders back in.
function prepare(html: string, cidUrls: Map<string, string>, allowRemote: boolean): Prepared {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const cids = new Set<string>();
  let remoteImages = 0;

  for (const tag of REMOVED_ELEMENTS) {
    for (const element of Array.from(doc.getElementsByTagName(tag))) {
      element.remove();
    }
  }
  // Inline event handlers can never fire in a scriptless sandbox, but a
  // message should not carry them into the frame at all. data:/vbscript:
  // URLs are stripped from navigation-capable attributes (G5: a data:text/html
  // link escapes the sandbox via allow-popups-to-escape-sandbox); media
  // sources keep data: because the frame CSP allows data: images and media.
  for (const element of Array.from(doc.querySelectorAll("*"))) {
    for (const attribute of Array.from(element.attributes)) {
      if (isUnsafeAttribute(attribute.name, attribute.value)) {
        element.removeAttribute(attribute.name);
      }
    }
  }

  for (const image of Array.from(doc.querySelectorAll("img"))) {
    image.removeAttribute("srcset");
    image.removeAttribute("loading");
    const src = image.getAttribute("src") ?? "";
    if (/^cid:/i.test(src)) {
      const cid = normalizeCid(src);
      cids.add(cid);
      const resolved = cidUrls.get(cid);
      if (resolved) {
        image.setAttribute("src", resolved);
      } else {
        image.setAttribute("src", TRANSPARENT_PIXEL);
        image.setAttribute("alt", image.getAttribute("alt") ?? "");
      }
    } else if (isRemote(src)) {
      remoteImages += 1;
      if (!allowRemote) {
        image.setAttribute("data-remote-src", src);
        image.setAttribute("src", TRANSPARENT_PIXEL);
      }
    }
  }
  // Remote backgrounds behave like remote images: counted, blocked by the
  // CSP until allowed (the attribute stays; the CSP decides).
  for (const element of Array.from(doc.querySelectorAll("[background]"))) {
    const background = element.getAttribute("background") ?? "";
    if (isRemote(background)) {
      remoteImages += 1;
    }
  }

  const styles = Array.from(doc.querySelectorAll("style"))
    .map((style) => style.textContent ?? "")
    .join("\n");
  const imageSources = `data: blob:${allowRemote ? " https: http:" : ""}`;
  const csp = [
    "default-src 'none'",
    `img-src ${imageSources}`,
    "style-src 'unsafe-inline'",
    "font-src data:",
    "media-src data: blob:",
  ].join("; ");

  const srcDoc =
    "<!doctype html><html><head>" +
    `<meta http-equiv="Content-Security-Policy" content="${csp}">` +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '<base target="_blank">' +
    `<style>${FRAME_STYLE}</style>` +
    `<style>${styles}</style>` +
    "</head><body>" +
    doc.body.innerHTML +
    "</body></html>";

  return { srcDoc, remoteImages, cids: Array.from(cids) };
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: contentType });
}

export function HtmlBody({ email, html }: { email: EmailMessage; html: string }) {
  const shell = useShell();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [allowRemote, setAllowRemote] = useState(false);
  const [cidUrls, setCidUrls] = useState<Map<string, string>>(() => new Map());
  const [height, setHeight] = useState(160);

  // EmailBody is keyed by message id, so a different message remounts this
  // component: remote-image consent is per message and the previous
  // message's blob URLs are revoked on unmount.

  const prepared = useMemo(() => prepare(html, cidUrls, allowRemote), [html, cidUrls, allowRemote]);

  // Resolve inline images through the shell. Blob URLs live in a ref and are
  // revoked only on unmount: revoking in an effect cleanup would kill a URL
  // the very moment the state update that references it re-runs the effect.
  // The effect is keyed on the attachments' identity, not on `email` (a new
  // object every snapshot), so a snapshot arriving mid-download cannot cancel
  // it; only unmount does.
  const createdUrls = useRef<string[]>([]);
  const requested = useRef<Set<string>>(new Set());
  const mounted = useRef(true);
  const attachments = email.attachments;
  useEffect(() => {
    // Set on every mount run: StrictMode simulates an unmount/remount in
    // development and the ref survives it.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const find = (cid: string): Attachment | undefined =>
      (attachments ?? []).find((candidate) => candidate.cid && normalizeCid(candidate.cid) === cid);
    // Only cids whose attachment is already in the snapshot get requested;
    // the rest stay pending so a later hydration picks them up.
    const missing = prepared.cids.filter(
      (cid) => !cidUrls.has(cid) && !requested.current.has(cid) && find(cid),
    );
    if (missing.length === 0) {
      return;
    }
    for (const cid of missing) {
      requested.current.add(cid);
    }
    (async () => {
      const resolved = new Map<string, string>();
      for (const cid of missing) {
        const attachment = find(cid);
        if (!attachment) {
          continue;
        }
        try {
          // The op returns {name, contentType, size, base64}.
          const outcome = (await shell.command({
            op: "downloadAttachment",
            attachment,
          } as never)) as { result?: { base64: string; contentType?: string | null } };
          const payload = outcome?.result;
          if (!payload) {
            continue;
          }
          const url = URL.createObjectURL(
            base64ToBlob(payload.base64, payload.contentType ?? attachment.type),
          );
          if (!mounted.current) {
            URL.revokeObjectURL(url);
            continue;
          }
          createdUrls.current.push(url);
          resolved.set(cid, url);
        } catch (cause) {
          console.warn("Lately: inline image failed to load", cid, cause);
        }
      }
      if (mounted.current && resolved.size > 0) {
        setCidUrls((previous) => {
          const next = new Map(previous);
          for (const [cid, url] of resolved) {
            next.set(cid, url);
          }
          return next;
        });
      }
    })();
  }, [prepared.cids, cidUrls, attachments, shell]);

  useEffect(() => {
    const urls = createdUrls.current;
    return () => {
      for (const url of urls) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  // Size the frame to its content: the frame is same-origin (and scriptless),
  // so the parent can read the document and follow image loads.
  const measure = () => {
    const frame = frameRef.current;
    const body = frame?.contentDocument?.documentElement;
    if (!body) {
      return;
    }
    const next = Math.min(Math.max(body.scrollHeight + 8, 120), 20000);
    setHeight(next);
  };
  const handleLoad = () => {
    measure();
    const doc = frameRef.current?.contentDocument;
    if (!doc) {
      return;
    }
    for (const image of Array.from(doc.images)) {
      if (!image.complete) {
        image.addEventListener("load", measure, { once: true });
        image.addEventListener("error", measure, { once: true });
      }
    }
    if (typeof ResizeObserver !== "undefined" && doc.body) {
      const observer = new ResizeObserver(() => measure());
      observer.observe(doc.body);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {prepared.remoteImages > 0 && !allowRemote ? (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
          <span>
            {prepared.remoteImages === 1
              ? "1 remote image blocked to protect your privacy."
              : `${prepared.remoteImages} remote images blocked to protect your privacy.`}
          </span>
          <button
            type="button"
            className="ml-3 rounded px-2 py-0.5 font-medium text-foreground hover:bg-muted"
            onClick={() => setAllowRemote(true)}
          >
            Load images
          </button>
        </div>
      ) : null}
      <iframe
        ref={frameRef}
        title="Message body"
        // No allow-scripts: same-origin only grants the parent measurement
        // access; the document itself stays inert.
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        srcDoc={prepared.srcDoc}
        onLoad={handleLoad}
        style={{ height }}
        className="w-full rounded-lg border border-border bg-white"
      />
    </div>
  );
}
